// apps/web/src/components/StepUpModal.tsx
//
// Step-up re-auth modal. Triggered when a step-up-gated action
// returns STEP_UP_REQUIRED. Per phase-1-5 §6, the prompt is a
// fresh signin OTP against the user's EXISTING contact. The backend
// then bumps the per-session recency and the original action can
// be retried.

import { useState } from 'react';
import { OtpInput } from './OtpInput.js';
import { Alert } from './ui/Alert.js';
import { Button } from './ui/Button.js';
import { apiPost, ApiError } from '../lib/apiClient.js';
import type { OtpSession } from '../lib/api.js';

export interface StepUpModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  contact: { type: 'email' | 'phone'; value: string };
}

export function StepUpModal({ open, onClose, onSuccess, contact }: StepUpModalProps): JSX.Element | null {
  const [code, setCode] = useState('');
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (code.length !== 6) return;
    setError(null);
    setSubmitting(true);
    try {
      // Step-up: trigger a signin OTP, then verify. The backend
      // updates sessions.last_reauthenticated_at on success.
      const signin = await apiPost<OtpSession>('/api/v1/auth/signin', {
        contact: { type: contact.type, value: contact.value },
      });
      if (!signin.data.sessionId) {
        setError({ message: 'Could not send a code to that contact.', requestId: signin.requestId });
        return;
      }
      const verify = await apiPost<{ verified: boolean; accessTokenExpiresIn?: number }>(
        '/api/v1/otp/verify',
        { sessionId: signin.data.sessionId, code },
      );
      if (verify.data.verified) {
        onSuccess();
      } else {
        setError({ message: 'Wrong code. Try again.', requestId: verify.requestId });
        setCode('');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setError({
        message: apiErr?.message ?? 'Could not complete step-up.',
        requestId: apiErr?.requestId ?? null,
      });
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-900">
        <h2 id="stepup-title" className="text-lg font-semibold">Confirm it&apos;s you</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          For security, we need a fresh code sent to your {contact.type}{' '}
          <span className="font-mono text-gray-700 dark:text-gray-300">
            ({contact.value.replace(/(.{2}).+(@.+)/, '$1•••$2')})
          </span>{' '}
          before you can do this.
        </p>

        <div className="mt-4">
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={() => { void submit(); }}
            legend="Enter the 6-digit code"
            disabled={submitting}
          />
        </div>

        {error ? <div className="mt-3"><Alert variant="error" requestId={error.requestId}>{error.message}</Alert></div> : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void submit()} isLoading={submitting} disabled={code.length !== 6}>
            Verify
          </Button>
        </div>
      </div>
    </div>
  );
}
